import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DeclineReasonService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  title: yup.string().required('formErrors.titleRequired'),
});

interface DeclineReasonState {
  title: string;
}

const DeclineReasonForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [declineReasonData, setDeclineReasonData] = useState<DeclineReasonState | null>(null);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<DeclineReasonState>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchDeclineReason = async () => {
        try {
          const response = await DeclineReasonService.getSingle(+id!);
          const declineReason = response;
          setDeclineReasonData({
            title: declineReason.title,
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchDeclineReason();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (declineReasonData) {
      reset(declineReasonData);
    }
  }, [declineReasonData, reset]);

  const onSubmit = async (data: DeclineReasonState) => {
    try {
      isEdit
        ? await DeclineReasonService.update(data, +id!)
        : await DeclineReasonService.create(data);

      SuccessToast(
        t(`declineReason.${isEdit ? "successUpdate" : "successCreate"}`)
      );
      navigate("/decline-reasons");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("declineReason.title")}</label>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.title ? "is-invalid" : ""}`}
                            placeholder={t("declineReason.enterTitle")}
                            {...field}
                          />
                        )}
                      />
                      {errors.title && <div className="invalid-feedback">
                        {t(errors.title.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`declineReason.${isEdit ? "update" : "create"}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default DeclineReasonForm;